import {
  Building2,
  GraduationCap,
  Inbox,
  LifeBuoy,
  ListTodo,
  type LucideIcon,
  Mail,
  Megaphone,
  NewspaperIcon,
  StoreIcon,
  Users,
  Workflow,
} from "lucide-react";

/** A single rich menu row: leading icon (lucide or brand image), title + blurb. */
export type MenuLink = {
  title: string;
  /** Optional supporting blurb. Omit for a title-only row. */
  description?: string;
  href: string;
  icon?: LucideIcon;
  /** Brand/integration logo path, used instead of a lucide icon. */
  iconImage?: string;
  /** Short text monogram, used when no brand logo is available. */
  monogram?: string;
};

/** Featured customer-story rail (Playground-style). */
export type CustomerStory = {
  eyebrow: string;
  quote: string;
  name: string;
  org: string;
  image: string;
  href: string;
};

/** Generic featured rail with a CTA. */
export type FeaturedCard = {
  eyebrow: string;
  title: string;
  description: string;
  href: string;
  cta: string;
};

/** App download rail: icon-led banner linking out to an app store. */
export type AppPromo = {
  eyebrow: string;
  title: string;
  /** External store URL, opened in a new tab. */
  href: string;
  cta: string;
};

export type NavEntry =
  | { type: "link"; title: string; href: string }
  | {
      type: "mega";
      title: string;
      /** Number of columns for the link grid. Defaults to 1. */
      columns?: 1 | 2;
      items: MenuLink[];
      story?: CustomerStory;
      featured?: FeaturedCard;
      appPromo?: AppPromo;
    };

const BARRE3_STORY: CustomerStory = {
  eyebrow: "Customer story",
  quote:
    "Helios is incredibly user friendly, and the team is always there whenever we need them. It just works.",
  name: "Lisa Shipley",
  org: "Barre3 West Loop",
  image: "/images/barre3-icon.png",
  href: "/blog",
};

const IOS_APP_PROMO: AppPromo = {
  eyebrow: "Helios for iPhone",
  title: "Calls, texts, emails, and tasks on the go.",
  href: "https://apps.apple.com/us/app/helios-sms-marketing/id1624968173",
  cta: "Download on the App Store",
};

export const MARKETING_NAV: NavEntry[] = [
  {
    type: "mega",
    title: "Platform",
    columns: 2,
    items: [
      {
        title: "Unified Inbox",
        description: "Every lead and member conversation in one place.",
        href: "/platform/unified-inbox",
        icon: Inbox,
      },
      {
        title: "Flows",
        description: "Lifecycle flows across text, email, and voice.",
        href: "/platform/flows",
        icon: Workflow,
      },
      {
        title: "Blasts",
        description: "Launch promos and win-back blasts in minutes.",
        href: "/platform/blasts",
        icon: Megaphone,
      },
      {
        title: "Email Builder",
        description: "On-brand, drag-and-drop email, no designer.",
        href: "/platform/email-builder",
        icon: Mail,
      },
      {
        title: "Customer Data",
        description: "One unified profile from every source you run.",
        href: "/platform/customer-data",
        icon: Users,
      },
      {
        title: "Tasks",
        description: "To-dos and follow-ups your team gets done.",
        href: "/platform/tasks",
        icon: ListTodo,
      },
    ],
    appPromo: IOS_APP_PROMO,
  },
  {
    type: "mega",
    title: "Switch to Helios",
    items: [
      { title: "From Loyalsnap", href: "/compare/loyalsnap-alternative" },
      { title: "From Klaviyo", href: "/compare/klaviyo-alternative" },
      { title: "From BrandBot", href: "/compare/brandbot-alternative" },
      { title: "From GoHighLevel", href: "/compare/gohighlevel-alternative" },
    ],
    featured: {
      eyebrow: "White-glove migration",
      title: "We move your data for you",
      description:
        "Our team imports your contacts, rebuilds your automations, and gets you live, usually within a week.",
      href: "/book-a-demo",
      cta: "See how switching works",
    },
  },
  {
    type: "mega",
    title: "Built for",
    items: [
      {
        title: "Boutique studios",
        description: "Convert more trials and fill every class.",
        href: "/built-for/studios",
        icon: StoreIcon,
      },
      {
        title: "Multi-location & franchises",
        description: "One consistent experience across every location.",
        href: "/built-for/multi-location",
        icon: Building2,
      },
    ],
  },
  {
    type: "link",
    title: "Pricing",
    href: "/pricing",
  },
  {
    type: "mega",
    title: "Resources",
    items: [
      {
        title: "Guides",
        description: "Deep dives on lifecycle marketing that converts.",
        href: "/guides",
        icon: GraduationCap,
      },
      {
        title: "Help center",
        description: "Answers for getting the most from Helios.",
        href: "/help-center",
        icon: LifeBuoy,
      },
      {
        title: "Changelog",
        description: "Stay updated on the latest features and improvements.",
        href: "/help-center/changelog",
        icon: NewspaperIcon,
      },
    ],
    story: BARRE3_STORY,
  },
];
